/**
 * Клиент API биллинга (https://billing.<домен>), авторизация токеном Kindata ID.
 * Все функции возвращают тело ответа или бросают BillingError.
 */
import {http, BILLING_API_URL, BILLING_PRODUCT} from "./host";
import {toBillingError} from "./errors";

function url(path) {
    return `${BILLING_API_URL}/v1${path}`
}

function accountUrl(company_id, path = '') {
    return url(`/accounts/${company_id}${path}`)
}

async function call(request) {
    try {
        const result = await request
        return result.data
    } catch (error) {
        throw toBillingError(error)
    }
}

/**
 * Публичные тарифы продукта, включая пакеты
 * @returns {Promise<Array>} [{id, code, name, description, products, entitlements, prices, trial_allowed}]
 */
export function getPlans(product = BILLING_PRODUCT) {
    return call(http.get(url('/plans'), {params: {product}, skipAuth: true}))
}

/**
 * Профиль оплаты, действующие подписки и последние счета
 * requisites_error — {code, message}, если DaData не нашла ИНН или организация ликвидирована
 * @returns {Promise<{account: Object, requisites_error: Object|null, card_saving_enabled: boolean,
 *   subscriptions: Array, invoices: Array}>}
 */
export function getAccount(company_id) {
    return call(http.get(accountUrl(company_id)))
}

/**
 * @param {Object} data - { payer_type?, edo_enabled?, documents_email? }
 */
export function updateAccount(company_id, data) {
    return call(http.patch(accountUrl(company_id), data))
}

/**
 * Исправить реквизиты плательщика. Заменяет все четыре поля: не переданное поле очищается.
 * ИНН меняется в Kindata ID, ОГРН берётся из DaData
 * @param {Object} data - { name, short_name, kpp, address }
 * @returns {Promise<Object>} профиль оплаты (account) с requisites.source = manual
 */
export function updateRequisites(company_id, data) {
    return call(http.put(accountUrl(company_id, '/requisites'), data))
}

/**
 * Предварительный расчёт счёта без сохранения (заодно проверяет промокод)
 * @param {Object} data - { plan_price_id, method, payer_type?, promo_code?, save_card? }
 */
export function getQuote(company_id, data) {
    return call(http.post(accountUrl(company_id, '/quote'), data))
}

/**
 * Выставить счёт. Для карты в ответе confirmation_url — страница оплаты ЮKassa
 */
export function checkout(company_id, data) {
    return call(http.post(accountUrl(company_id, '/checkout'), data))
}

export function getInvoices(company_id, {limit = 50, offset = 0} = {}) {
    return call(http.get(accountUrl(company_id, '/invoices'), {params: {limit, offset}}))
}

export function getInvoice(company_id, invoice_id) {
    return call(http.get(accountUrl(company_id, `/invoices/${invoice_id}`)))
}

/**
 * Оплатить счёт картой/СБП (в том числе повторно после отказа).
 * save_card биллинг игнорирует, пока сохранение карт выключено (card_saving_enabled = false)
 * @returns {Promise<{invoice_id, confirmation_url, status}>}
 */
export function payInvoice(company_id, invoice_id, save_card = false) {
    return call(http.post(accountUrl(company_id, `/invoices/${invoice_id}/pay`), {save_card}))
}

export function getPaymentMethods(company_id) {
    return call(http.get(accountUrl(company_id, '/payment-methods')))
}

export function deletePaymentMethod(company_id, method_id) {
    return call(http.delete(accountUrl(company_id, `/payment-methods/${method_id}`)))
}

export function setAutoRenew(company_id, subscription_id, enabled) {
    return call(http.post(accountUrl(company_id, `/subscriptions/${subscription_id}/auto-renew`), {enabled}))
}

// Отмена в конце оплаченного периода
export function cancelSubscription(company_id, subscription_id) {
    return call(http.post(accountUrl(company_id, `/subscriptions/${subscription_id}/cancel`)))
}
